import { type ReactNode } from 'react';
import { Cards, Info, Skeleton, Stamp } from '../../../../modules/design-system';
import type {
  PoolShippingService,
  ShippingServiceCodeName,
} from '../../../../modules/api';
import { PromoPrice } from '../../../../modules/shippingPromo';

export interface ShippingOptionsSectionProps {
  services: PoolShippingService[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** No cards picked yet → can't quote anything; prompt to choose items. */
  hasItems: boolean;
  /** No address yet → can't quote the carrier rate; prompt for an address. */
  hasAddress: boolean;
  /** Full EasyPost price per option (service id → $), backend-quoted. */
  optionPrices?: Record<number, number>;
  /** Regular price per option while a promotion lowers them — struck through
   *  before the option's price where it is higher. */
  originalOptionPrices?: Record<number, number>;
  /** Quote in flight → show loaders in place of the per-option prices. */
  loading?: boolean;
  /** `code_name` of the protection tier the backend requires for this
   *  shipment — the option matching it gets a "Required" badge. Null when
   *  no protection is mandated. */
  requiredCodeName?: ShippingServiceCodeName | null;
  /** Clicking the "choose items" empty state opens the in-page item picker. */
  onChooseItems?: () => void;
  /** Clicking the "enter address" empty state opens the address flow. */
  onChooseAddress?: () => void;
}

/** Small "Required" pill on the option the backend mandates. Colours via
 *  `--co-ShippingOptionRequired-*` with hex fallbacks. */
function RequiredBadge() {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase leading-[14px] tracking-[0.4px]"
      style={{
        background: 'var(--co-ShippingOptionRequired-fill, rgba(35,209,29,0.16))',
        color: 'var(--co-ShippingOptionRequired-text, #23D11D)',
      }}
    >
      Required
    </span>
  );
}

/**
 * Centered icon + copy block used for the two pre-quote empty states. When
 * `onClick` is given the card becomes a button (hover + pointer) so the prompt
 * is directly actionable — clicking it opens the matching picker.
 */
function EmptyState({
  icon,
  children,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick?: () => void;
}) {
  const cardStyle = {
    border:
      '1px solid var(--co-DeliveryShippingOptions-emptyStateBorder, rgba(171,174,179,0.16))',
    color: 'var(--co-DeliveryShippingOptions-emptyStateText, rgba(255,255,255,0.70))',
    background:
      'linear-gradient(to bottom, ' +
      'var(--co-DeliveryShippingOptions-emptyStateBgFrom, rgba(255,255,255,0.03)) 0%, ' +
      'var(--co-DeliveryShippingOptions-emptyStateBgTo, rgba(255,255,255,0.03)) 100%)',
  };
  const body = (
    <>
      {icon}
      <p className="mt-3 text-[14px] leading-[20px]">{children}</p>
    </>
  );
  return (
    <>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="mt-3 flex w-full cursor-pointer flex-col items-center justify-center rounded-[20px] bg-white/[0.03] px-4 py-10 text-center transition-colors hover:bg-white/[0.07]"
          style={cardStyle}
        >
          {body}
        </button>
      ) : (
        <div
          className="mt-3 flex flex-col items-center justify-center rounded-[20px] bg-white/[0.03] px-4 py-10 text-center"
          style={cardStyle}
        >
          {body}
        </div>
      )}
      <div
        className="mt-3 flex items-center gap-2 text-[12px]"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        <Info size={16} strokeWidth={1.6} />
        <span>Shipping costs include payment processing fee</span>
      </div>
    </>
  );
}

/**
 * "Delivery Shipping Options" section. Until both items AND an address exist
 * we can't quote, so we show a prompt (choose items first, then address). Once
 * quotable we render the service catalogue as a single-select radio list with
 * the per-option EasyPost price (or a loader while the quote is in flight).
 */
export function ShippingOptionsSection({
  services,
  selectedId,
  onSelect,
  hasItems,
  hasAddress,
  optionPrices = {},
  originalOptionPrices = {},
  loading = false,
  requiredCodeName = null,
  onChooseItems,
  onChooseAddress,
}: ShippingOptionsSectionProps) {
  return (
    <section data-co-component="DeliveryShippingOptions">
      <h3
        className="font-bold tracking-[-0.3px]"
        style={{
          color: 'var(--co-DeliveryShippingOptions-titleColor, #FFFFFF)',
          fontSize: 'var(--co-DeliveryShippingOptions-titleSize, 17px)',
        }}
      >
        Delivery Shipping Options
      </h3>

      {!hasItems ? (
        <EmptyState
          icon={<Cards size={28} strokeWidth={1.6} />}
          {...(onChooseItems ? { onClick: onChooseItems } : {})}
        >
          <span className="font-semibold text-white">Choose items to ship</span>
          <br />
          to see delivery options
        </EmptyState>
      ) : !hasAddress || services.length === 0 ? (
        <EmptyState
          icon={<Stamp size={28} strokeWidth={1.6} />}
          {...(onChooseAddress ? { onClick: onChooseAddress } : {})}
        >
          <span className="font-semibold text-white">To calculate the cost</span> of delivery,
          <br />
          please enter your address
        </EmptyState>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {services.map((s) => {
            const isActive = s.id === selectedId;
            const price = optionPrices[s.id];
            const isRequired =
              requiredCodeName != null && s.codeName === requiredCodeName;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSelect(s.id)}
                  aria-pressed={isActive}
                  className="
                    flex w-full items-start justify-between gap-3 rounded-[16px]
                    p-3 text-left transition-colors
                  "
                  style={{
                    background: isActive
                      ? 'linear-gradient(to bottom, ' +
                        'var(--co-DeliveryShippingOptions-optionActiveBgFrom, rgba(35,209,29,0.10)) 0%, ' +
                        'var(--co-DeliveryShippingOptions-optionActiveBgTo, rgba(35,209,29,0.10)) 100%)'
                      : 'linear-gradient(to bottom, ' +
                        'var(--co-DeliveryShippingOptions-optionIdleBgFrom, rgba(255,255,255,0.04)) 0%, ' +
                        'var(--co-DeliveryShippingOptions-optionIdleBgTo, rgba(255,255,255,0.04)) 100%)',
                    border:
                      '1px solid ' +
                      (isActive
                        ? 'var(--co-DeliveryShippingOptions-optionActiveBorder, #23D11D)'
                        : 'var(--co-DeliveryShippingOptions-optionIdleBorder, rgba(255,255,255,0.08))'),
                    color: 'var(--co-DeliveryShippingOptions-optionText, #FFFFFF)',
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold">{s.name}</span>
                      {isRequired && <RequiredBadge />}
                    </div>
                    {s.description && (
                      <div
                        className="mt-0.5 text-[12px]"
                        style={{
                          color:
                            'var(--co-DeliveryShippingOptions-optionDescText, rgba(255,255,255,0.55))',
                        }}
                      >
                        {s.description}
                      </div>
                    )}
                  </div>
                  {loading ? (
                    <Skeleton className="mt-0.5 h-[18px] w-[52px] shrink-0 rounded" />
                  ) : (
                    <div
                      className="shrink-0 text-[14px] font-semibold tabular-nums"
                      style={{
                        color:
                          price == null
                            ? 'var(--co-DeliveryShippingOptions-optionDescText, rgba(255,255,255,0.55))'
                            : 'var(--co-DeliveryShippingOptions-optionPriceText, #FFFFFF)',
                      }}
                    >
                      {price == null ? (
                        'TBD'
                      ) : (
                        <PromoPrice amount={price} original={originalOptionPrices[s.id]} />
                      )}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
